import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, ShoppingBag, ArrowRight, Gift } from "lucide-react";
import { useCart } from "../lib/cart";
import api, { FILE_BASE } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";

export default function Cart() {
  const { items, remove, total, clear, searchContext } = useCart();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [bundles, setBundles] = useState([]);

  useEffect(() => {
    api.get("/bundles").then((r) => setBundles(r.data.bundles || [])).catch(() => {});
  }, []);

  // Compute best bundle price locally for preview
  const qty = items.length;
  const samePrice = qty > 0 && new Set(items.map((i) => i.price_sek)).size === 1;
  const unitPrice = items[0]?.price_sek || 0;
  let bestBundle = null;
  let bestTotal = total;
  if (samePrice) {
    for (const b of bundles) {
      if (qty >= b.min_qty && qty <= b.max_qty) {
        const bundleTotal = total * (1 - (b.discount_pct || 0));
        if (bundleTotal < total && (bestBundle === null || bundleTotal < bestTotal)) {
          bestBundle = b;
          bestTotal = bundleTotal;
        }
      }
    }
  }
  const discount = Math.max(0, total - bestTotal);
  const finalTotal = Math.min(total, bestTotal);

  // Suggest next bundle if close to unlocking
  let suggestion = null;
  if (samePrice && !bestBundle) {
    for (const b of bundles) {
      if (qty < b.min_qty) {
        const missing = b.min_qty - qty;
        if (suggestion === null || missing < suggestion.missing) {
          suggestion = { bundle: b, missing };
        }
      }
    }
  }

  const checkout = async (e) => {
    e.preventDefault();
    setError("");
    if (items.length === 0) return;
    setLoading(true);
    try {
      const r = await api.post("/checkout/session", {
        photo_ids: items.map((i) => i.id),
        customer_email: email,
        customer_name: name,
        customer_phone: phone || null,
        bib_searched: searchContext?.bib || null,
        event_id_searched: searchContext?.event_id || null,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (err) {
      setError(err?.response?.data?.detail || "Kunde inte starta betalning");
      setLoading(false);
    }
  };

  return (
    <div className="App">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <p className="label-overline mb-2">Varukorg</p>
        <h1 className="font-display font-black uppercase text-white text-4xl sm:text-5xl mb-8">
          Din beställning
        </h1>

        {items.length === 0 ? (
          <div className="card-surface p-12 text-center" data-testid="cart-empty">
            <ShoppingBag className="w-12 h-12 mx-auto text-white/20 mb-4" />
            <p className="font-display uppercase text-2xl text-white mb-4">Varukorgen är tom</p>
            <Link to="/search" className="btn-primary inline-flex items-center gap-2">
              Börja söka <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-3" data-testid="cart-items">
              <p className="text-sm text-white/50">
                Foton från <span className="text-brand font-bold">{items[0].photographer_name}</span>
              </p>
              {items.map((p) => (
                <div key={p.id} className="card-surface flex items-center gap-4 p-3" data-testid={`cart-item-${p.id}`}>
                  <img src={`${FILE_BASE}${p.thumb_url}`} alt={p.event_name} className="w-24 h-20 object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-white/50 uppercase tracking-wider truncate">{p.photographer_name}</p>
                    <p className="text-white truncate">{p.event_name}</p>
                    {[...(p.detected_numbers || []), ...(p.manual_numbers || [])].slice(0, 2).map((n) => (
                      <span key={n} className="inline-block mr-1 mt-1 bg-brand/20 text-brand text-xs font-bold px-2 py-0.5">#{n}</span>
                    ))}
                  </div>
                  <p className="font-display font-bold text-brand">{p.price_sek?.toFixed(0)} kr</p>
                  <button
                    onClick={() => remove(p.id)}
                    className="text-white/50 hover:text-destructive p-2"
                    data-testid={`cart-remove-${p.id}`}
                    aria-label="Ta bort"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button onClick={clear} className="btn-ghost text-xs" data-testid="cart-clear">
                Töm varukorg
              </button>
            </div>

            <div className="card-surface p-6 h-fit lg:sticky lg:top-24">
              <h2 className="font-display font-black uppercase text-white text-2xl mb-1">Sammanfattning</h2>
              <div className="border-b border-white/10 py-4 my-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-white/60">Foton</span>
                  <span className="text-white">{items.length} st</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/60">Delsumma</span>
                  <span className="text-white">{total.toFixed(0)} kr</span>
                </div>
                {bestBundle && (
                  <div className="flex justify-between text-sm" data-testid="bundle-discount">
                    <span className="text-brand flex items-center gap-1"><Gift className="w-3 h-3" /> {bestBundle.name}-rabatt</span>
                    <span className="text-brand">−{discount.toFixed(0)} kr</span>
                  </div>
                )}
              </div>

              {suggestion && (
                <div className="bg-brand/10 border-l-4 border-brand p-3 text-xs text-white/80 mb-4" data-testid="bundle-suggestion">
                  💡 Lägg till <strong className="text-brand">{suggestion.missing}</strong> {suggestion.missing === 1 ? "foto till" : "foton till"} för {suggestion.bundle.name} (spara {Math.round((suggestion.bundle.discount_pct || 0) * 100)}%).
                </div>
              )}

              <div className="flex justify-between mb-6">
                <span className="font-display uppercase text-white">Att betala</span>
                <span className="font-display font-black text-brand text-2xl" data-testid="cart-total">
                  {finalTotal.toFixed(0)} kr
                </span>
              </div>
              <p className="text-xs text-white/40 mb-4">Alla priser inkl. 25% moms.</p>

              <form onSubmit={checkout} className="space-y-3" data-testid="checkout-form">
                <div>
                  <label className="label-overline block mb-1">Namn</label>
                  <input required value={name} onChange={(e) => setName(e.target.value)} className="input-field" data-testid="checkout-name" />
                </div>
                <div>
                  <label className="label-overline block mb-1">E-post (för leverans)</label>
                  <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-field" data-testid="checkout-email" />
                </div>
                <div>
                  <label className="label-overline block mb-1">Telefon (frivilligt)</label>
                  <input value={phone} onChange={(e) => setPhone(e.target.value)} className="input-field" data-testid="checkout-phone" />
                </div>
                {error && <p className="text-sm text-destructive" data-testid="checkout-error">{error}</p>}
                <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-50" data-testid="checkout-submit">
                  {loading ? "Skapar betalning..." : `Gå till betalning · ${finalTotal.toFixed(0)} kr`}
                </button>
                <p className="text-xs text-white/40 text-center">
                  Säker betalning via Stripe. Stöttar fotograf & förare.
                </p>
              </form>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
